import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SxProps, Theme } from '@mui/material/styles'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import NoResults from '@/assets/svg/no-results.svg?react'

import { ExpertiseStatus } from '@/core/models/expertise.model'
import ExpertiseIcon from '@/components/ui/icons/Expertise'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import ExpertisesTable from '@/pages/expertises/containers/ExpertisesTable'
import ExpertisesCardViewContainer from '@/pages/expertises/containers/ExpertisesCardViews'

export type ExpertiseData = {
  id: string
  creator: string
  creatorGender?: 'F' | 'M' | 'I' | 'O'
  doctor?: string
  doctorGender?: 'F' | 'M' | 'I' | 'O'
  isReviewable?: boolean
  patient: string
  patientGender?: 'F' | 'M' | 'I' | 'O'
  startDate: string
  status: ExpertiseStatus
}

export type ExpertisesCardProps = {
  isDoctor?: boolean
  expertises: ExpertiseData[]
  onFilterChange?: (filter: string) => void
  emptyMessage: string
  filters?: {
    id: string
    title: string
    activeColor: string
    badgeContent: number
    selected: boolean
  }[]
  sx?: SxProps<Theme>
}


export default function ExpertisesCard(props: ExpertisesCardProps) {
  const { isDoctor = false, expertises, emptyMessage, filters = [], onFilterChange, sx = [] } = props
  const { t } = useTranslation()

  const isMobile = useIsMobile()

  return (
    <Card sx={[{ height: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        paddingX={3}
        paddingY={3}
        spacing={2}
      >
        <Stack divider={<Divider orientation="vertical" flexItem />} direction="row" justifyContent="flex-start" alignItems="center" spacing={3}>
          {filters.map((filter) => {
            return (
              <Button
                key={filter.id}
                variant="neutral"
                onClick={() => onFilterChange?.(filter.id)}
                sx={[
                  { color: filter.selected ? filter.activeColor : 'grey', cursor: 'pointer', fontWeight: 500 },
                  filter.selected && {
                    borderColor: filter.activeColor,
                  },
                ]}
              >
                <Badge
                  key={filter.id}
                  invisible={false}
                  badgeContent={filter.badgeContent}
                  color="primary"
                  showZero
                  sx={{
                    '& .MuiBadge-badge': {
                      right: '-16px',
                      top: '-6px',
                      padding: '0 4px',
                      fontSize: 12,
                      height: 24,
                      minWidth: 24,
                      borderRadius: '100%',
                      backgroundColor: filter.activeColor,
                    },
                  }}
                >
                  {filter.title}
                </Badge>
              </Button>
            )
          })}
        </Stack>
        {!isDoctor && (
          <Button
            sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
            component={Link}
            to="/expertises/create"
            variant="contained"
            startIcon={<ExpertiseIcon htmlColor="#FFF" />}
          >
            {t('page.home.expertises.createExpertiseButton.label')}
          </Button>
        )}
      </Stack>
      <Box sx={{ padding: '0 24px 24px 24px' }}>
        {!expertises.length ? (
          <Stack direction="column" justifyContent="center" alignItems="center">
            <CardMedia component={NoResults} sx={{ width: 'auto', height: '250px', mt: '10px', mr: '40px' }} />
            <Typography component="div" variant="body1">
              {emptyMessage}
            </Typography>
          </Stack>
        ) : (
          <>
            {isMobile && (<ExpertisesCardViewContainer />)}
            {!isMobile && (<ExpertisesTable />)}

          </>
        )
        }
      </Box>
    </Card>
  )
}
